import React, { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { MessageCircle, X, Send, Sparkles, UserRound } from "lucide-react";

// Floating Game Master chat widget — a small bubble in the bottom-right that
// expands into a 360x520 chat panel. Session-scoped (no auth, no history
// persisted). Lazy-mounted on first interaction so it costs ~0 paint cost.

const STORAGE_KEY = "motowear_gm_session";

export default function GameMasterWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Welcome to MotoWear. I'm your Game Master. Ask me about shipping, sizing, custom orders — or whisper 'clue' if you're hunting the cipher." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const [escForm, setEscForm] = useState({ email: "", name: "", phone: "", message: "" });
  const [escBusy, setEscBusy] = useState(false);
  const [escMsg, setEscMsg] = useState("");
  const sessionRef = useRef(typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) || "" : "");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput(""); setBusy(true);
    try {
      const r = await client.post("/chat/game-master", { message: text, session_id: sessionRef.current || undefined });
      if (r.data?.session_id) {
        sessionRef.current = r.data.session_id;
        try { localStorage.setItem(STORAGE_KEY, r.data.session_id); } catch (_) { /* localStorage unavailable */ }
      }
      setMessages((m) => [...m, { role: "bot", text: r.data?.reply || "…", ai: !!r.data?.ai_powered }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: "Signal lost. Try once more." }]);
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-5 z-40 bg-black text-white w-14 h-14 rounded-full shadow-lg hover:bg-[#FF3B30] transition-colors flex items-center justify-center"
        aria-label="Open chat"
        data-testid="gm-widget-open"
      >
        <MessageCircle size={22} strokeWidth={1.75} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 right-5 z-40 w-[360px] max-w-[calc(100vw-2.5rem)] bg-white hairline-strong shadow-2xl flex flex-col" style={{ height: 520 }} data-testid="gm-widget-panel">
      <div className="flex items-center justify-between p-4 border-b border-black/10 bg-black text-white">
        <div className="flex items-center gap-2">
          <Sparkles size={14} strokeWidth={2.5} className="text-[#FF3B30]" />
          <div className="font-display text-lg tracking-tight leading-none">Game Master</div>
        </div>
        <button onClick={() => setOpen(false)} className="text-white/55 hover:text-white" aria-label="Close" data-testid="gm-widget-close"><X size={18} /></button>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" data-testid="gm-widget-messages">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={(m.role === "user" ? "bg-black text-white" : "bg-[#FAFAFA] text-black") + " px-3 py-2 max-w-[85%] whitespace-pre-wrap leading-relaxed"}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && <div className="text-xs text-black/40 italic" data-testid="gm-widget-typing">Game Master is typing…</div>}
      </div>
      <form onSubmit={send} className="border-t border-black/10 p-3 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={1000}
          placeholder="Ask anything…"
          className="flex-1 border-b border-black/30 focus:border-black bg-transparent py-1.5 text-sm focus:outline-none"
          data-testid="gm-widget-input"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          className="bg-black text-white px-3 hover:bg-[#FF3B30] disabled:opacity-40 transition-colors flex items-center"
          data-testid="gm-widget-send"
        >
          <Send size={14} strokeWidth={2} />
        </button>
      </form>
      <div className="px-3 pb-3">
        <button
          type="button"
          onClick={() => setShowEscalate(true)}
          className="w-full text-[10px] tracking-[0.22em] uppercase text-black/45 hover:text-[#FF3B30] flex items-center justify-center gap-2 py-1"
          data-testid="gm-widget-escalate-trigger"
        >
          <UserRound size={11} strokeWidth={2} aria-hidden="true" />
          Talk to a human instead
        </button>
      </div>
      {showEscalate && (
        <div className="absolute inset-0 bg-white z-10 flex flex-col" data-testid="gm-widget-escalate-panel">
          <div className="flex items-center justify-between p-4 border-b border-black/10 bg-[#FFF5F4]">
            <div>
              <div className="label-eyebrow moto-accent text-[10px]">Live agent handoff</div>
              <div className="font-display text-lg tracking-tight mt-1">Get a human reply</div>
            </div>
            <button
              type="button"
              onClick={() => { setShowEscalate(false); setEscMsg(""); }}
              className="text-black/55 hover:text-black"
              aria-label="Back to chat"
              data-testid="gm-widget-escalate-close"
            >
              <X size={18} />
            </button>
          </div>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setEscBusy(true); setEscMsg("");
              try {
                const history = messages.map((m) => ({ role: m.role === "user" ? "user" : "assistant", content: m.text }));
                const r = await client.post("/chat/game-master/escalate", {
                  email: escForm.email.trim(),
                  name: escForm.name.trim(),
                  phone: escForm.phone.trim() || null,
                  message: escForm.message.trim() || "(no extra message — see transcript)",
                  session_id: sessionRef.current || undefined,
                  history,
                });
                setEscMsg(`Got it — ticket ${r.data.ticket_id.slice(0, 8)} is open. A human will reply to ${escForm.email} within a business day.`);
                setEscForm({ email: "", name: "", phone: "", message: "" });
              } catch (err) {
                setEscMsg(err.response?.data?.detail || "Couldn't reach the team — please email service@motowear.co.uk directly.");
              } finally {
                setEscBusy(false);
              }
            }}
            className="flex-1 overflow-y-auto p-4 space-y-3 text-sm"
          >
            <p className="text-xs text-black/60 leading-relaxed">
              Fill this in and we&apos;ll pass your full chat to a real person. Reply lands in your inbox within one business day.
            </p>
            <input
              required
              type="email"
              value={escForm.email}
              onChange={(e) => setEscForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="Your email *"
              className="w-full border-b border-black/30 bg-transparent py-2 text-sm focus:outline-none focus:border-black"
              data-testid="gm-widget-escalate-email"
            />
            <input
              type="text"
              value={escForm.name}
              onChange={(e) => setEscForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Your name"
              className="w-full border-b border-black/30 bg-transparent py-2 text-sm focus:outline-none focus:border-black"
              data-testid="gm-widget-escalate-name"
            />
            <input
              type="tel"
              value={escForm.phone}
              onChange={(e) => setEscForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="Phone (optional)"
              className="w-full border-b border-black/30 bg-transparent py-2 text-sm focus:outline-none focus:border-black"
              data-testid="gm-widget-escalate-phone"
            />
            <textarea
              value={escForm.message}
              onChange={(e) => setEscForm((f) => ({ ...f, message: e.target.value }))}
              placeholder="Anything extra to tell the team? (optional)"
              rows={3}
              maxLength={4000}
              className="w-full border border-black/20 p-2 text-sm focus:outline-none focus:border-black resize-none"
              data-testid="gm-widget-escalate-message"
            />
            {escMsg && <div className="text-xs text-black/70 bg-[#FFF5F4] p-2" data-testid="gm-widget-escalate-msg">{escMsg}</div>}
            <button
              type="submit"
              disabled={escBusy}
              className="w-full bg-black text-white py-3 label-eyebrow text-xs hover:bg-[#FF3B30] disabled:opacity-40 transition-colors"
              data-testid="gm-widget-escalate-submit"
            >
              {escBusy ? "Sending…" : "Send to the team"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
